import { createDefaultIfElseConfig, isConditionStep, normalizeConditionConfig } from './workflowConditionUtils';
import {
  createStepFromCatalogAction,
  insertCatalogActionAtIndex,
} from './workflowBuilderAddAction';
import { createStepsFromCatalogAction } from './workflowStepGrouping';
import { copyStep, generateNewStepIds } from './workflowStepReorder';

export function createMainAddTarget(insertIndex) {
  return { type: 'main', insertIndex };
}

export function createBranchAddTarget({
  conditionStepId,
  branchId,
  insertIndex,
  parentPath = [],
}) {
  return {
    type: 'branch',
    conditionStepId,
    branchId,
    insertIndex,
    parentPath: Array.isArray(parentPath) ? parentPath : [],
  };
}

function resolveBranchKey(config, branchId) {
  const normalized = normalizeConditionConfig(config);
  if (branchId === 'none' || branchId === normalized.noneBranch?.id) {
    return 'none';
  }
  return branchId;
}

export function getBranchStepsFromConfig(config, branchId) {
  const normalized = normalizeConditionConfig(config);
  const key = resolveBranchKey(normalized, branchId);
  if (key === 'none') {
    return normalized.noneBranch?.steps || [];
  }
  return normalized.branches.find((branch) => branch.id === key)?.steps || [];
}

function updateBranchStepsOnConditionStep(conditionStep, branchId, updater) {
  const config = normalizeConditionConfig(conditionStep.config);
  const key = resolveBranchKey(config, branchId);

  if (key === 'none') {
    const nextBranchSteps = updater(config.noneBranch?.steps || []);
    return {
      ...conditionStep,
      config: {
        ...config,
        noneBranch: {
          ...config.noneBranch,
          steps: nextBranchSteps,
        },
      },
    };
  }

  return {
    ...conditionStep,
    config: {
      ...config,
      branches: (config.branches || []).map((branch) => (
        branch.id === key
          ? { ...branch, steps: updater(branch.steps || []) }
          : branch
      )),
    },
  };
}

function applyToConditionStep(steps, parentPath, conditionStepId, mutator) {
  if (!parentPath?.length) {
    return steps.map((step) => (
      step.id === conditionStepId ? mutator(step) : step
    ));
  }

  const [segment, ...rest] = parentPath;
  return updateConditionStepBranchSteps(
    steps,
    { conditionStepId: segment.conditionStepId, branchId: segment.branchId, parentPath: [] },
    (branchSteps) => applyToConditionStep(branchSteps, rest, conditionStepId, mutator),
  );
}

export function updateConditionStepBranchSteps(steps, target, branchIdOrUpdater, maybeUpdater) {
  let conditionStepId;
  let branchId;
  let parentPath = [];
  let updater;

  if (typeof target === 'object' && target !== null && target.conditionStepId) {
    ({ conditionStepId, branchId, parentPath = [] } = target);
    updater = branchIdOrUpdater;
  } else {
    conditionStepId = target;
    branchId = branchIdOrUpdater;
    updater = maybeUpdater;
  }

  return applyToConditionStep(steps, parentPath, conditionStepId, (conditionStep) => (
    updateBranchStepsOnConditionStep(conditionStep, branchId, updater)
  ));
}

function findBranchStepLocationInSteps(steps, stepId, parentPath = []) {
  for (const step of steps || []) {
    if (!isConditionStep(step)) continue;
    const config = normalizeConditionConfig(step.config);

    for (const branch of config.branches || []) {
      for (let index = 0; index < (branch.steps || []).length; index += 1) {
        const child = branch.steps[index];
        if (child.id === stepId) {
          return {
            conditionStepId: step.id,
            branchId: branch.id,
            stepIndex: index,
            isNone: false,
            parentPath,
          };
        }
        if (isConditionStep(child)) {
          const nested = findBranchStepLocationInSteps(
            [child],
            stepId,
            [...parentPath, { conditionStepId: step.id, branchId: branch.id }],
          );
          if (nested) return nested;
        }
      }
    }

    for (let index = 0; index < (config.noneBranch?.steps || []).length; index += 1) {
      const child = config.noneBranch.steps[index];
      if (child.id === stepId) {
        return {
          conditionStepId: step.id,
          branchId: config.noneBranch?.id || 'none',
          stepIndex: index,
          isNone: true,
          parentPath,
        };
      }
      if (isConditionStep(child)) {
        const nested = findBranchStepLocationInSteps(
          [child],
          stepId,
          [...parentPath, { conditionStepId: step.id, branchId: config.noneBranch?.id || 'none' }],
        );
        if (nested) return nested;
      }
    }
  }

  return null;
}

export function findBranchStepLocation(steps, stepId) {
  if (!stepId) return null;
  return findBranchStepLocationInSteps(steps, stepId, []);
}

export function updateBranchStepInCondition(steps, location, updatedStep) {
  const { conditionStepId, branchId, parentPath = [] } = location;
  return updateConditionStepBranchSteps(
    steps,
    { conditionStepId, branchId, parentPath },
    (branchSteps) => branchSteps.map((step) => (step.id === updatedStep.id ? updatedStep : step)),
  );
}

export function removeBranchStepFromCondition(steps, location, stepId) {
  const { conditionStepId, branchId, parentPath = [] } = location;
  return updateConditionStepBranchSteps(
    steps,
    { conditionStepId, branchId, parentPath },
    (branchSteps) => branchSteps.filter((step) => step.id !== stepId),
  );
}

export function duplicateBranchStepAfter(steps, location, stepId) {
  const { conditionStepId, branchId, parentPath = [] } = location;
  let duplicatedStep = null;
  const nextSteps = updateConditionStepBranchSteps(
    steps,
    { conditionStepId, branchId, parentPath },
    (branchSteps) => {
      const index = branchSteps.findIndex((step) => step.id === stepId);
      if (index < 0) return branchSteps;
      [duplicatedStep] = generateNewStepIds([copyStep(branchSteps[index])]);
      const next = [...branchSteps];
      next.splice(index + 1, 0, duplicatedStep);
      return next;
    },
  );

  return { steps: nextSteps, duplicatedStep };
}

function insertStepsIntoBranch(steps, target, insertedSteps) {
  const { conditionStepId, branchId, insertIndex, parentPath = [] } = target;
  return updateConditionStepBranchSteps(
    steps,
    { conditionStepId, branchId, parentPath },
    (branchSteps) => {
      const next = [...branchSteps];
      const at = insertIndex != null && insertIndex >= 0 ? insertIndex : next.length;
      next.splice(at, 0, ...insertedSteps);
      return next;
    },
  );
}

/**
 * Insert a catalog action into the main lane or a condition branch child list.
 */
export function insertCatalogActionAtTarget(steps, target, action, options = {}) {
  if (!target || target.type === 'main') {
    const result = insertCatalogActionAtIndex(steps, target?.insertIndex ?? null, action, options);
    return {
      ...result,
      insertedTarget: createMainAddTarget(target?.insertIndex ?? null),
    };
  }

  const timestamp = options.timestamp ?? Date.now();
  const groupedSteps = createStepsFromCatalogAction(action, timestamp);

  if (groupedSteps?.length) {
    const primaryStep = groupedSteps[0];
    const newSteps = insertStepsIntoBranch(steps, target, groupedSteps);
    return {
      newSteps,
      insertedSteps: groupedSteps,
      primaryStep,
      isGroup: true,
      insertedTarget: target,
    };
  }

  const primaryStep = createStepFromCatalogAction(action, { timestamp });
  const newSteps = insertStepsIntoBranch(steps, target, [primaryStep]);

  return {
    newSteps,
    insertedSteps: [primaryStep],
    primaryStep,
    isGroup: false,
    insertedTarget: target,
  };
}

export function ensureConditionBranchStepsShape(config = {}) {
  const normalized = normalizeConditionConfig(config);
  return {
    ...normalized,
    branches: (normalized.branches || []).map((branch) => ({
      ...branch,
      steps: Array.isArray(branch.steps) ? branch.steps : [],
    })),
    noneBranch: {
      ...createDefaultIfElseConfig().noneBranch,
      ...normalized.noneBranch,
      steps: Array.isArray(normalized.noneBranch?.steps) ? normalized.noneBranch.steps : [],
    },
  };
}
